/* workspace-admin/WorkspaceConsoleLayout — workspace-scoped admin shell.
   Same nx-cc-* console design as Platform/Org tiers, scoped to one workspace
   (:wsId). Sidenav: Overview · Membership · Settings.
   Topbar: context-aware back button, workspace identity, UserMenu with logout. */
import React, { useCallback, useEffect } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Pill } from '../shared/ui';
import { PlatformFooter } from '../platform/PlatformLayout';
import { statusTone, findWorkspace } from '../platform/platformStore';
import { useRbac } from '../rbac/RbacContext';
import { AccessDenied } from '../rbac/guards';
import { roleFromSession, ROLES } from '../rbac/roles';
import UserMenu from '../shared/auth/UserMenu.jsx';
import { STATUS_LABEL } from './workspaceConstants.js';
import { applyBrandingTokens, clearBrandingTokens } from '../shared/branding/index.js';
import * as brandingApi from '../api/branding.api.js';

const navItems = (wsId) => [
  { group: 'Workspace', items: [
    { to: `/workspaces/${wsId}`,                        end: true, label: 'Overview',      icon: 'LayoutDashboard' },
    { to: `/workspaces/${wsId}/members`,                           label: 'Membership',    icon: 'Users'           },
    { to: `/workspaces/${wsId}/modules`,                           label: 'Modules',       icon: 'Layers'          },
    { to: `/workspaces/${wsId}/audit`,                             label: 'Audit Log',     icon: 'ClipboardList'   },
    { to: `/workspaces/${wsId}/settings`,                          label: 'Settings',      icon: 'Settings'        },
  ]},
  { group: 'CRM Configuration', items: [
    { to: `/workspaces/${wsId}/customization/fields`,              label: 'Custom Fields',   icon: 'Hash'            },
    { to: `/workspaces/${wsId}/customization/objects`,             label: 'Custom Objects',  icon: 'Database'        },
  ]},
];

export default function WorkspaceConsoleLayout({ data, children }) {
  const { wsId } = useParams();
  const navigate = useNavigate();
  const { signOut, switchWorkspace } = window.useAuth();
  const { orgId } = useRbac();

  /* CRM-WORKSPACE-002 BUG-1: Apply workspace-specific white-label for the URL workspace.
     Previous implementation fetched resolved-branding (which inherits org colors) and
     applied it via the global branding helper, causing org green to bleed into the
     Workspace Admin shell when no workspace WL is configured.
     Fix: always fetch the workspace-own WL (which defaults to #125AB0 blue when unconfigured)
     and apply tokens directly — bypassing the resolved inheritance chain entirely.
     Tokens are cleared on mount and cleanup so branding doesn't persist across routes. */
  useEffect(() => {
    if (!wsId) return;
    clearBrandingTokens();
    let cancelled = false;
    brandingApi.getWhiteLabel(wsId).then((wl) => {
      if (!cancelled) applyBrandingTokens(wl);
    }).catch(() => {});
    return () => {
      cancelled = true;
      clearBrandingTokens();
    };
  }, [wsId]);

  /* Derive role/context from live session — used for layout labels and scope check. */
  const sessionUser = globalThis.__nx_auth?.user;
  const isSA = sessionUser?.platformRole === 'SUPER_ADMIN';

  const found = findWorkspace(data.state, wsId);
  // RG-006: non-SA users may only access workspaces belonging to their own org
  const ws = found && (isSA || found.org === orgId) ? found : null;

  const handleLogout = useCallback(async () => {
    await signOut().catch(() => {});
    navigate('/', { replace: true });
  }, [signOut, navigate]);
  const normalizedRole = roleFromSession(sessionUser?.effectiveRole || sessionUser?.platformRole);
  const isOA = normalizedRole === ROLES.ORG_ADMIN;
  const isWA = normalizedRole === ROLES.WS_ADMIN;
  const roleLabel = isSA ? 'Super Admin' : isOA ? 'Org Admin' : isWA ? 'WS Admin' : 'WS Member';
  const roleTone  = isSA ? 'purple' : isOA ? 'blue' : 'green';

  /* Context-aware exit:
     SA → Control Center | OA → Org Console | WA → Open CRM (switch workspace first) */
  const org = ws ? data.state.organizations.find(o => o.id === ws.org) : null;
  const exitLabel = isSA ? 'Control Center'
    : isOA ? (org?.name || 'Organization')
    : 'Open CRM';

  const handleExit = useCallback(async () => {
    if (isSA) { navigate('/platform'); return; }
    if (isOA) { navigate(org ? `/organization/${org.id}` : '/workspaces'); return; }
    /* WA/member — switch session to this workspace then open CRM */
    if (switchWorkspace) {
      try { await switchWorkspace(wsId); } catch (_) {}
    }
    navigate('/app/' + wsId);
  }, [isSA, isOA, org, wsId, navigate, switchWorkspace]);

  /* Profile link: SA → platform profile, OA → org profile, WA → none (use CRM ProfileMenu) */
  const onProfile = isSA ? () => navigate('/platform/profile')
    : isOA && org ? () => navigate(`/organization/${org.id}/profile`)
    : undefined;

  const isLoading = Boolean(data.state.loading);

  if (!ws) {
    // found exists but org doesn't match — cross-org access attempt
    if (found) return <AccessDenied action="membership.view" fallbackTo="/workspaces" />;
    return (
      <div className="nx-cc-root">
        <div className="nx-cc-topbar">
          <button className="nx-cc-exit-btn" onClick={handleExit}>
            <NexusIcon name="ChevronLeft" size={14} />
            <span>{exitLabel}</span>
          </button>
          <div className="nx-cc-topbar-brand">
            <span className="nx-cc-topbar-title">{isLoading ? 'Loading…' : 'Workspace not found'}</span>
          </div>
          <div className="nx-cc-topbar-right" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <UserMenu roleLabel={roleLabel} roleTone={roleTone} onLogout={handleLogout} onProfile={onProfile} />
          </div>
        </div>
        {isLoading ? (
          <div className="nx-cc-workspace">
            <div className="nx-cc-sidenav">
              {['s1','s2','s3','s4','s5'].map(k => (
                <div key={k} style={{ height: 32, margin: '2px 8px', borderRadius: 6, background: 'var(--nx-surface-2, #222938)', opacity: 0.3 }} />
              ))}
            </div>
            <div className="nx-cc-content" />
          </div>
        ) : (
          <div className="nx-cc-content"><div className="nx-empty" style={{ marginTop: 60 }}><NexusIcon name="LayoutGrid" size={24} /><span>This workspace does not exist.</span></div></div>
        )}
      </div>
    );
  }

  const NAV = navItems(wsId);

  return (
    <div className="nx-cc-root nx-org-root">
      <div className="nx-cc-topbar">
        <button className="nx-cc-exit-btn" onClick={handleExit}>
          <NexusIcon name="ChevronLeft" size={14} />
          <span>{exitLabel}</span>
        </button>
        <div className="nx-cc-topbar-brand" style={{ cursor: 'pointer' }} onClick={() => navigate(`/workspaces/${wsId}`)}>
          <span className="nx-cc-topbar-icon nx-org-icon">{ws.name?.[0] ?? '?'}</span>
          <span className="nx-cc-topbar-title">{ws.name}</span>
          <Pill tone="green">Workspace Console</Pill>
          <Pill tone={statusTone((ws.status ?? '').toUpperCase())}>{STATUS_LABEL[ws.status] || ws.status}</Pill>
        </div>
        <div className="nx-cc-topbar-right" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <UserMenu roleLabel={roleLabel} roleTone={roleTone} onLogout={handleLogout} onProfile={onProfile} />
        </div>
      </div>
      <div className="nx-cc-workspace">
        <div className="nx-cc-sidenav">
          {NAV.map(grp => (
            <div key={grp.group} className="nx-cc-nav-group">
              <span className="nx-cc-nav-group-label">{grp.group}</span>
              {grp.items.map(it => (
                <NavLink key={it.to} to={it.to} end={it.end}
                  className={({ isActive }) => 'nx-cc-nav-item' + (isActive ? ' is-active' : '')}>
                  <NexusIcon name={it.icon} size={15} /><span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>
        <div className="nx-cc-content">{children}</div>
      </div>
      <PlatformFooter />
    </div>
  );
}
